// src/components/Colegiado/Certificados.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Box,
    Button,
    Stack,
    Typography,
    Divider,
    Alert,
    CircularProgress,
    Chip,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";

/**
 * Endpoints:
 *  - GET /colegiado/perfil
 *  - GET /inscripciones/mias
 *  - GET /api/colegiados/{codigo}/historial 
 *  - POST /api/convert/docx-to-pdf
 */

export default function Certificados() {
    const API = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    const { token } = useAuth();
    const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const [downloading, setDownloading] = useState({});
    const [certMsg, setCertMsg] = useState(null);

    const [cargandoIns, setCargandoIns] = useState(false);
    const [inscripciones, setInscripciones] = useState([]);

    const [perfil, setPerfil] = useState(null);
    const [cargandoPerfil, setCargandoPerfil] = useState(true);

    // Recibos / bloqueo
    const [comprobandoRecibos, setComprobandoRecibos] = useState(false);
    const [tienePendientes, setTienePendientes] = useState(false);
    const [errorRecibos, setErrorRecibos] = useState(null);

    // ==== helpers de fechas/strings ====
    const toDate = (v) => {
        if (!v) return null;
        const d = new Date(v);
        return isNaN(d.getTime()) ? null : d;
    };
    const fmtDateES = (v) => {
        const d = toDate(v);
        if (!d) return "";
        return d.toLocaleDateString("es-ES");
    };
    const fechaLargaES = (date = new Date()) => {
        const meses = [
            "enero", "febrero", "marzo", "abril", "mayo", "junio",
            "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
        ];
        return `Valladolid, a ${date.getDate()} de ${meses[date.getMonth()]} de ${date.getFullYear()}`;
    };
    const nombreCompleto = (p) => {
        if (!p) return "";
        const parts = [
            (p.nombre || "").toString().trim(),
            (p.apellido_1 || p.apellido1 || "").toString().trim(),
            (p.apellido_2 || p.apellido2 || "").toString().trim(),
        ].filter(Boolean);
        return parts.join(" ");
    };

    // ==== cargar perfil ====
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setCargandoPerfil(true);
                const res = await axios.get(`${API}/colegiado/perfil`, { headers: authHeader });
                if (!alive) return;
                setPerfil(res?.data || null);
            } catch (e) {
                console.error("No se pudo cargar el perfil:", e);
                setPerfil(null);
            } finally {
                if (alive) setCargandoPerfil(false);
            }
        })();
        return () => { alive = false; };
    }, [API, authHeader]);

    // ==== comprobar recibos pendientes ====
    useEffect(() => {
        if (!perfil) return;
        let alive = true;

        const getCodigo = () =>
            perfil?.codigo ?? perfil?.colegiado_codigo ?? perfil?.colegiadoCodigo ?? null;

        (async () => {
            const codigo = getCodigo();
            if (!codigo) return;
            try {
                setComprobandoRecibos(true);
                setErrorRecibos(null);
                const url = `${API}/api/colegiados/${encodeURIComponent(codigo)}/historial`;
                const res = await axios.get(url, { headers: authHeader });

                if (!alive) return;

                const raw = res?.data;
                const recibos = Array.isArray(raw) ? raw : Array.isArray(raw?.recibos) ? raw.recibos : [];

                const estado = (r) =>
                    String(r.resultado_banco ?? r.resultadoBanco ?? "PENDIENTE").toUpperCase();

                const hayPend = recibos.some((r) => {
                    const e = estado(r);
                    return e === "PENDIENTE" || e === "PENDIENTE_DEVUELTO";
                });

                setTienePendientes(hayPend);
            } catch (e) {
                console.error("Error comprobando recibos:", e);
                setErrorRecibos("No se pudo comprobar el estado de sus recibos.");
                setTienePendientes(true); // por seguridad, bloquear
            } finally {
                if (alive) setComprobandoRecibos(false);
            }
        })();

        return () => { alive = false; };
    }, [API, authHeader, perfil]);

    // ==== cargar inscripciones (PAGADA o FINALIZADA) ====
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setCargandoIns(true);
                const res = await axios.get(`${API}/inscripciones/mias`, { headers: authHeader });
                if (!alive) return;

                const arr = Array.isArray(res?.data) ? res.data : [];
                const validas = arr.filter((i) => {
                    const st = String(i?.estado ?? "").toUpperCase();
                    return st === "PAGADA" || st === "FINALIZADA";
                });

                const norm = validas
                    .map((i) => {
                        const c = i?.curso || {};
                        const cursoNombre = c.nombre ?? c.titulo ?? c.nombreCurso ?? `Curso ${c.id ?? ""}`;
                        return {
                            idInscripcion: i.id,
                            cursoId: c.id ?? c.codigo ?? null,
                            cursoNombre: String(cursoNombre ?? "").trim(),
                        };
                    })
                    .filter((x, idx, self) => {
                        const key = `${x.cursoId ?? "null"}__${x.cursoNombre}`;
                        return self.findIndex((y) => `${y.cursoId ?? "null"}__${y.cursoNombre}` === key) === idx;
                    });

                setInscripciones(norm);
            } catch (e) {
                console.warn("No se pudieron cargar inscripciones:", e);
                setInscripciones([]);
            } finally {
                if (alive) setCargandoIns(false);
            }
        })();
        return () => { alive = false; };
    }, [API, authHeader]);

    const buildPlantillaUrl = (path) => {
        if (!path) return path;
        // Si ya es absoluta (http/https), la dejamos tal cual
        if (/^https?:\/\//i.test(path)) return path;

        const origin = window.location.origin.replace(/\/+$/, ""); // https://app.enfernet-valladolid.org
        const cleanPath = String(path).replace(/^\/+/, "");        // certificados/...
        return `${origin}/${cleanPath}`;
    };

    // ===== Docx->PDF genérico (con nullGetter para evitar 'undefined') =====
    const generarYDescargarDesdePlantilla = async ({
        plantillaPath,
        nombreSalida,
        data,
        claveEstado,
    }) => {
        try {
            setDownloading((d) => ({ ...d, [claveEstado]: true }));
            setCertMsg(null);

            const plantillaUrl = buildPlantillaUrl(plantillaPath);
            console.debug("[Certificados] Cargando plantilla desde:", plantillaUrl);

            const resp = await fetch(plantillaUrl, { cache: "no-store" });
            if (!resp.ok) throw new Error(`No se encontró la plantilla en ${plantillaUrl}`);
            const arrayBuffer = await resp.arrayBuffer();
            const zip = new PizZip(arrayBuffer);

            const doc = new Docxtemplater(zip, {
                paragraphLoop: true,
                linebreaks: true,
                delimiters: { start: "{", end: "}" },
                nullGetter: () => "",
            });
            doc.setData(data);
            doc.render();
            const docxBlob = doc.getZip().generate({
                type: "blob",
                mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });

            const form = new FormData();
            const base = nombreSalida.replace(/\.pdf$/i, "");
            form.append("file", docxBlob, `${base}.docx`);

            const pdfResp = await axios.post(`${API}/api/convert/docx-to-pdf`, form, {
                headers: { ...authHeader, "Content-Type": "multipart/form-data" },
                responseType: "blob",
            });

            const pdfBlob = new Blob([pdfResp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${base}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);

            setCertMsg({ tipo: "success", texto: "Certificado generado correctamente." });
        } catch (e) {
            console.error(e);
            setCertMsg({
                tipo: "error",
                texto: e?.response?.data || e?.message || "No se pudo generar el certificado.",
            });
        } finally {
            setDownloading((d) => ({ ...d, [claveEstado]: false }));
        }
    };

    // ===== Colegiación (definitivo) =====
    const descargarColegiacion = async () => {
        if (!perfil) return;
        const data = {
            NOMBRE_COMPLETO: nombreCompleto(perfil),
            //FECHA ALTA
            FECHA_1A_COLEGIACION: fmtDateES(perfil.fecha_alta_colegiacion || perfil.fechaAltaColegiacion),
            CODIGO: String(perfil.codigo ?? perfil.colegiado_codigo ?? perfil.colegiadoCodigo ?? "").trim(),
            FECHA_LARGA: fechaLargaES(new Date()),
        };
        await generarYDescargarDesdePlantilla({
            plantillaPath: "/certificados/certificado_colegiacion.docx",
            nombreSalida: `Certificado_Colegiacion_${(data.NOMBRE_COMPLETO || "Colegiado").replace(/\s+/g, "_")}.pdf`,
            data,
            claveEstado: "colegiacion",
        });
    };

    // ===== Provisional (1º mes) =====
    const fechaPrimera = useMemo(
        () => toDate(perfil?.fecha_alta_colegiacion || perfil?.fechaAltaColegiacion),
        [perfil]
    );
    const dentroDeVentanaProvisional = useMemo(() => {
        if (!fechaPrimera) return false;
        const msDiff = Date.now() - fechaPrimera.getTime();
        const dias = msDiff / (1000 * 60 * 60 * 24);
        return dias >= 0 && dias <= 31;
    }, [fechaPrimera]);

    const descargarProvisional = async () => {
        if (!perfil) return;
        const data = {
            NOMBRE_COMPLETO: nombreCompleto(perfil),
            FECHA_1A_COLEGIACION: fmtDateES(perfil.fecha_alta_colegiacion || perfil.fechaAltaColegiacion),
            CODIGO: String(perfil.codigo ?? perfil.colegiado_codigo ?? perfil.colegiadoCodigo ?? "").trim(),
            FECHA_LARGA: fechaLargaES(new Date()),
        };
        await generarYDescargarDesdePlantilla({
            plantillaPath: "/certificados/certificado_colegiacion_provisional.docx",
            nombreSalida: `Certificado_Colegiacion_Provisional_${(data.NOMBRE_COMPLETO || "Colegiado").replace(/\s+/g, "_")}.pdf`,
            data,
            claveEstado: "provisional",
        });
    };

    // ===== Cursos =====
    const slug = (s = "") =>
        String(s || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-zA-Z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "")
            .toLowerCase();

    const descargarCurso = async (cursoNombre) => {
        if (!perfil) return;

        const nombreCursoSeguro = String(cursoNombre ?? "").trim();
        const data = {
            NOMBRE_COMPLETO: nombreCompleto(perfil),
            CODIGO: String(perfil.codigo ?? perfil.colegiado_codigo ?? perfil.colegiadoCodigo ?? "").trim(),

            // ====== ALIAS PARA LA PLANTILLA ======
            CURSO_NOMBRE: nombreCursoSeguro,
            CURSO: nombreCursoSeguro,
            NOMBRE_CURSO: nombreCursoSeguro,
            TITULO_CURSO: nombreCursoSeguro,

            FECHA_LARGA: fechaLargaES(new Date()),
        };

        // debug útil si en la plantilla sigue sin salir:
        console.debug("[Certificados] Datos enviados a plantilla de curso:", data);

        const plantillaPath = "/certificados/curso.docx";

        await generarYDescargarDesdePlantilla({
            plantillaPath,
            nombreSalida: `Certificado_Asistencia_${slug(nombreCursoSeguro)}_${slug(data.NOMBRE_COMPLETO)}.pdf`,
            data,
            claveEstado: `curso_${nombreCursoSeguro}`,
        });
    };

    // ==== bloqueo por estado (colegiación) ====
    const ESTADOS_BLOQUEO_COLEGIACION = new Set([7, 8, 9, 10, 11, 12, 13]);

    const getEstadoIdPerfil = (p) => {
        if (!p) return null;

        // casos típicos: estadoId / estado_id
        const direct =
            p.estadoId ??
            p.estado_id ??
            p.estadoID ??
            null;

        if (direct != null && direct !== "") return Number(direct);

        // a veces viene como objeto estado: { id: 7, ... }
        const nested =
            p.estado?.id ??
            p.estado?.estadoId ??
            p.estado?.estado_id ??
            null;

        if (nested != null && nested !== "") return Number(nested);

        // si por algún motivo viene como string numérico en otro campo
        return null;
    };

    const estadoIdPerfil = useMemo(() => getEstadoIdPerfil(perfil), [perfil]);
    const bloqueoPorEstadoColegiacion = useMemo(() => {
        if (estadoIdPerfil == null || Number.isNaN(estadoIdPerfil)) return false;
        return ESTADOS_BLOQUEO_COLEGIACION.has(Number(estadoIdPerfil));
    }, [estadoIdPerfil]);


    // ==== UI ====
    // Definitivo: bloquea por pendientes + comprobación + estado
    const bloqueoColegiacionDefinitivo =
        !cargandoPerfil && (tienePendientes || comprobandoRecibos || bloqueoPorEstadoColegiacion);

    // Provisional: NO bloquea por pendientes (ni por comprobación); solo por estado
    const bloqueoColegiacionProvisional =
        !cargandoPerfil && bloqueoPorEstadoColegiacion;

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Certificados
            </Typography>

            <Typography variant="body2" sx={{ mb: 2 }}>
                Pulsa un botón para descargar tu certificado en PDF con tus datos.
            </Typography>

            {!cargandoPerfil && (bloqueoPorEstadoColegiacion || tienePendientes || comprobandoRecibos) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {bloqueoPorEstadoColegiacion
                        ? "No puede descargar certificados de colegiación debido a su estado actual. Puede seguir descargando certificados de cursos."
                        : comprobandoRecibos
                            ? "Comprobando el estado de sus recibos… (esto solo afecta al certificado definitivo de colegiación)"
                            : tienePendientes
                                ? "El certificado DEFINITIVO de colegiación está bloqueado mientras existan recibos pendientes."
                                : null}
                </Alert>
            )}
            {errorRecibos && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorRecibos}
                </Alert>
            )}

            <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
                {!cargandoPerfil && perfil && dentroDeVentanaProvisional ? (
                    <Box>
                        <Button
                            variant="contained"
                            // ✅ Provisional NO se bloquea por recibos pendientes
                            disabled={bloqueoColegiacionProvisional || !!downloading["provisional"]}
                            onClick={descargarProvisional}
                            sx={{ alignSelf: "flex-start" }}
                        >
                            {downloading["provisional"] ? "Generando…" : "Colegiación Provisional"}
                        </Button>
                        <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.8 }}>
                            Disponible durante 1 mes desde {fmtDateES(fechaPrimera)}.
                        </Typography>
                    </Box>
                ) : (
                    <Button
                        variant="contained"
                        // ✅ Definitivo sí se bloquea por pendientes
                        disabled={
                            bloqueoColegiacionDefinitivo ||
                            !!downloading["colegiacion"] ||
                            cargandoPerfil ||
                            !perfil
                        }
                        onClick={descargarColegiacion}
                        sx={{ alignSelf: "flex-start" }}
                    >
                        {downloading["colegiacion"] ? "Generando…" : "Certificado de Colegiación"}
                    </Button>
                )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6">Certificados de asistencia a cursos</Typography>
                <Chip size="small" label="Solo cursos FINALIZADOS" />
            </Stack>

            {cargandoIns ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Cargando inscripciones…</Typography>
                </Box>
            ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {inscripciones.length === 0 && (
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            No tienes cursos con inscripción <strong>FINALIZADA</strong>.
                        </Typography>
                    )}

                    {inscripciones.map((it) => {
                        const key = `curso_${it.cursoId ?? it.cursoNombre}`;
                        const nombreCurso = it.cursoNombre || `Curso ${it.cursoId ?? ""}`;
                        return (
                            <Button
                                key={key}
                                variant="contained"
                                size="small"
                                disabled={!!downloading[`curso_${nombreCurso}`] || !perfil}
                                onClick={() => descargarCurso(nombreCurso)}
                                sx={{ mb: 1, alignSelf: "flex-start" }}
                            >
                                {downloading[`curso_${nombreCurso}`]
                                    ? "Generando…"
                                    : `Certificado — ${nombreCurso}`}
                            </Button>
                        );
                    })}
                </Stack>
            )}

            {certMsg && (
                <Box mt={2}>
                    <Alert severity={certMsg.tipo} onClose={() => setCertMsg(null)}>
                        {certMsg.texto}
                    </Alert>
                </Box>
            )}
        </Box>
    );
}
